import type { PID, RecordHints, WrappedResponse } from '@fs/zion-tree-types'
import { type SwrCustomizedResponse, useSwrRequest } from './useSwrRequest'
import { useRecordHintLevelPref } from '../providers/RecordHintLevelPrefProvider'

function isWrappedErrorResponse(data: WrappedResponse): boolean {
  return data?.data === null && Boolean(data?.statuses?.[0]?.cause ?? data?.status)
}

const transformResponse = (data: WrappedResponse<RecordHints>): RecordHints => {
  if (isWrappedErrorResponse(data)) {
    throw new Error(data.statuses?.[0]?.cause ?? data.status)
  }
  return data.data ?? { matches: [], nonMatches: [] }
}

const getUrl = (pid: string, showWeakerHints?: boolean): string => {
  return showWeakerHints
    ? `/service/tree/tree-data/record-matches/${pid}/all?hintsOnLiving=true&minConfidence=3`
    : `/service/tree/tree-data/record-matches/${pid}/all?hintsOnLiving=true`
}
type Props = {
  personId: PID | undefined
}

export default function useRecordHints({ personId }: Props): SwrCustomizedResponse<RecordHints, 'recordHints'> {
  const [{ recordHintLevel }] = useRecordHintLevelPref()
  const showWeakerHints = recordHintLevel === 3
  const url = personId ? getUrl(personId, showWeakerHints) : null

  const {
    data: recordHints,
    error,
    isLoading,
    isValidating,
    mutate: mutateRecordHints,
  } = useSwrRequest<RecordHints, WrappedResponse<RecordHints>>(url, undefined, {
    transform: transformResponse,
  })

  return { recordHints, error, isLoading, isValidating, mutateRecordHints }
}
